import { useCallback, useState } from "react";

import { NotificationPreferenceResponse } from "@/api";
import { PageLoader, Switch } from "@/components";
import {
  useNotificationPreferences,
  useUpdateNotificationPreferences,
} from "@/hooks";
import { useTranslate } from "@/providers/context";

type Switch = {
  property: keyof NotificationPreferenceResponse;
  title: string;
  description: string;
};

const SWITCHES: Switch[] = [
  {
    property: "postByAdmins",
    title: "Post by Admins",
    description:
      "Receive a notification when the Community Admin posts a new post.",
  },
  {
    property: "commentsAndReplies",
    title: "Comments and Replies",
    description:
      "Receive a notification when someone comments or replies to you.",
  },
  {
    property: "reactions",
    title: "Reactions",
    description:
      "Receive a notification when someone likes or reacts to your posts and comments.",
  },
  {
    property: "newPosts",
    title: "New Posts",
    description:
      "Receive a notification when there's a new post in the Community.",
  },
];

export const NotificationSettings = () => {
  const { translate } = useTranslate();
  const { data: notificationPreferences, isError } =
    useNotificationPreferences();
  const [pendingValues, setPendingValues] = useState<
    Partial<NotificationPreferenceResponse>
  >({});

  const updatePreferences = useUpdateNotificationPreferences();

  const handleToggle = useCallback(
    (property: keyof NotificationPreferenceResponse, checked: boolean) => {
      if (pendingValues[property]) {
        // Prevent spamming
        return;
      }

      setPendingValues((prev) => ({ ...prev, [property]: checked }));

      updatePreferences.mutate(
        { [property]: checked },
        {
          onSuccess: () => {
            setPendingValues((prev) => {
              const newPending = { ...prev };
              delete newPending[property];
              return newPending;
            });
          },
        },
      );
    },
    [updatePreferences, pendingValues],
  );

  if (isError) {
    return (
      <div className="p1-regular flex h-full items-center justify-center px-14 text-center text-item-secondary">
        {translate("Something went wrong, please try again later.")}
      </div>
    );
  }

  if (!notificationPreferences) {
    return <PageLoader />;
  }

  return (
    <div className="flex flex-col gap-8">
      {SWITCHES.map(({ property, title, description }) => (
        <div key={property} className="flex justify-between gap-7">
          <div className="flex flex-col gap-3">
            <div className="p1-semibold">{translate(title)}</div>
            <div className="p1-regular text-item-secondary">
              {translate(description)}
            </div>
          </div>
          <div>
            <Switch
              onCheckedChange={(v) => handleToggle(property, v)}
              checked={
                (pendingValues[property] as boolean) ??
                (notificationPreferences[property] as boolean)
              }
            />
          </div>
        </div>
      ))}
    </div>
  );
};
